import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import { Seo } from '@/components/Seo';
import SharedFooter from '@/components/feature/SharedFooter';
import { useToast } from '@/contexts/ToastContext';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import ContactV2 from '@/pages/info/v2/ContactV2';

export default function ContactPage() {
  const { isNew } = useSiteDesign();
  return isNew ? <ContactV2 /> : <ClassicContactPage />;
}

function ClassicContactPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [messageError, setMessageError] = useState('');

  const handleSuccess = () => {
    setStatus('success');
    showToast(t('contact.success.toast'));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageEl = form.elements.namedItem('message') as HTMLTextAreaElement;
    const messageValue = messageEl?.value || '';

    if (messageValue.length < 20) {
      setMessageError(t('contact.form.messageMinError'));
      return;
    }
    setMessageError('');
    setStatus('submitting');

    const data = new URLSearchParams();
    const fields = form.querySelectorAll('input[name], textarea[name], select[name]');
    fields.forEach((f) => {
      const el = f as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.type === 'checkbox') {
        data.append(el.name, (el as HTMLInputElement).checked ? 'yes' : 'no');
      } else {
        data.append(el.name, el.value);
      }
    });

    fetch('https://readdy.ai/api/form/d88su5aj08n3rflih9cg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => handleSuccess())
      .catch(() => handleSuccess());
  };

  const resetForm = () => {
    setStatus('idle');
    setMessageError('');
  };

  return (
    <>
      <main className="w-full min-h-screen bg-white font-sans">
        <Seo pageKey="contact" />
        <SharedNavbar />

        {/* ===== HERO ===== */}
        <section className="w-full bg-[#1A1A1A] pt-28 pb-16 md:pt-32 md:pb-20 px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-6 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('contact.hero.label')}
              </span>
              <div className="w-6 h-px bg-[#7B1113]" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-4">
              {t('contact.hero.heading')}
            </h1>
            <p className="text-sm text-white/40 font-light max-w-lg mx-auto">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* ===== SECTION 1 — CHANNELS ===== */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12 lg:px-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Email */}
              <div className="flex flex-col gap-4 p-6 rounded-2xl bg-[#F0EFED]">
                <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] flex items-center justify-center">
                  <i className="ri-mail-line text-white text-sm" />
                </div>
                <div>
                  <span className="text-[11px] text-[#8A8A8A] uppercase tracking-wide block">{t('contact.channels.email.label')}</span>
                  <p className="text-sm text-[#2A2A2A] font-medium mt-1">{t('contact.channels.email.value')}</p>
                  <p className="text-[11px] text-[#8A8A8A] font-light mt-1">{t('contact.channels.email.note')}</p>
                </div>
              </div>

              {/* WhatsApp / Phone */}
              <div className="flex flex-col gap-4 p-6 rounded-2xl bg-[#F0EFED]">
                <div className="w-10 h-10 rounded-xl bg-[#2D6A4F] flex items-center justify-center">
                  <i className="ri-phone-line text-white text-sm" />
                </div>
                <div>
                  <span className="text-[11px] text-[#8A8A8A] uppercase tracking-wide block">{t('contact.channels.whatsapp.label')}</span>
                  <p className="text-sm text-[#2A2A2A] font-medium mt-1">{t('contact.channels.whatsapp.value')}</p>
                  <p className="text-[11px] text-[#8A8A8A] font-light mt-1">{t('contact.channels.whatsapp.note')}</p>
                </div>
              </div>

              {/* Korea HQ */}
              <div className="flex flex-col gap-4 p-6 rounded-2xl bg-[#F0EFED]">
                <div className="w-10 h-10 rounded-xl bg-[#1B3A5C] flex items-center justify-center">
                  <i className="ri-building-line text-white text-sm" />
                </div>
                <div>
                  <span className="text-[11px] text-[#8A8A8A] uppercase tracking-wide block">{t('contact.channels.hq.label')}</span>
                  <p className="text-sm text-[#2A2A2A] font-medium mt-1">{t('contact.channels.hq.value')}</p>
                  <p className="text-[11px] text-[#8A8A8A] font-light mt-1">{t('contact.channels.hq.note')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECTION 2 — INQUIRY FORM ===== */}
        <section className="w-full bg-[#F0EFED] py-16 md:py-24 px-6 md:px-12 lg:px-20">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-6 md:p-10">
              <h2 className="font-serif text-2xl font-bold text-[#2A2A2A] mb-6">
                {t('contact.sendMessage.heading')}
              </h2>

              {status === 'success' ? (
                <div className="flex flex-col items-center text-center gap-4 py-8">
                  <div className="w-12 h-12 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                    <i className="ri-check-line text-white text-xl" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#2A2A2A]">{t('contact.success.heading')}</h3>
                  <p className="text-sm text-[#8A8A8A] font-light">{t('contact.success.body')}</p>
                  <button
                    onClick={resetForm}
                    className="mt-2 px-6 py-2.5 border border-[#2A2A2A]/20 text-[#2A2A2A] text-xs tracking-[0.12em] uppercase rounded-full hover:bg-[#2A2A2A] hover:text-white transition-all cursor-pointer text-center"
                  >
                    {t('contact.form.sendAnother')}
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d88su5aj08n3rflih9cg"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[11px] text-[#8A8A8A] uppercase tracking-wide mb-1.5 block">
                        {t('contact.form.name')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder={t('contact.form.namePlaceholder')}
                        className="w-full px-4 py-3 bg-[#F0EFED] border border-transparent rounded-xl text-sm text-[#2A2A2A] placeholder-[#8A8A8A]/50 focus:outline-none focus:border-[#7B1113] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-[#8A8A8A] uppercase tracking-wide mb-1.5 block">
                        {t('contact.form.email')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder={t('contact.form.emailPlaceholder')}
                        className="w-full px-4 py-3 bg-[#F0EFED] border border-transparent rounded-xl text-sm text-[#2A2A2A] placeholder-[#8A8A8A]/50 focus:outline-none focus:border-[#7B1113] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[11px] text-[#8A8A8A] uppercase tracking-wide mb-1.5 block">
                        {t('contact.form.phone')}
                      </label>
                      <input
                        type="text"
                        name="phone"
                        placeholder={t('contact.form.phoneHelper')}
                        className="w-full px-4 py-3 bg-[#F0EFED] border border-transparent rounded-xl text-sm text-[#2A2A2A] placeholder-[#8A8A8A]/50 focus:outline-none focus:border-[#7B1113] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-[#8A8A8A] uppercase tracking-wide mb-1.5 block">
                        {t('contact.form.subject')} *
                      </label>
                      <select
                        name="subject"
                        required
                        className="w-full px-4 py-3 bg-[#F0EFED] border border-transparent rounded-xl text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113] transition-colors appearance-none cursor-pointer"
                      >
                        <option value="">{t('contact.form.subjectPlaceholder')}</option>
                        <option value="Product Inquiry">{t('contact.form.subjectProductInquiry')}</option>
                        <option value="Pharmacy Partnership">{t('contact.form.subjectPharmacyPartnership')}</option>
                        <option value="Influencer Collaboration">{t('contact.form.subjectInfluencer')}</option>
                        <option value="Medi.K Seoul Medical Tourism Inquiry">{t('contact.form.subjectMediK')}</option>
                        <option value="Pick.K Seoul Influencer Program">{t('contact.form.subjectPickK')}</option>
                        <option value="Counterfeit Report">{t('contact.form.subjectCounterfeit')}</option>
                        <option value="Other">{t('contact.form.subjectOther')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] text-[#8A8A8A] uppercase tracking-wide mb-1.5 block">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder={t('contact.form.messagePlaceholder')}
                      className="w-full px-4 py-3 bg-[#F0EFED] border border-transparent rounded-xl text-sm text-[#2A2A2A] placeholder-[#8A8A8A]/50 focus:outline-none focus:border-[#7B1113] transition-colors resize-none"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-[10px] text-[#8A8A8A] font-light">{t('contact.form.maxChars')}</p>
                      {messageError && (
                        <p className="text-[10px] text-[#7B1113] font-light">{messageError}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="consent"
                      required
                      id="contact-consent"
                      className="mt-0.5 w-4 h-4 rounded border-[#8A8A8A]/30 text-[#7B1113] focus:ring-[#7B1113] cursor-pointer"
                    />
                    <label htmlFor="contact-consent" className="text-xs text-[#8A8A8A] font-light leading-relaxed cursor-pointer">
                      {t('contact.form.consent')}
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="mt-2 w-full sm:w-auto px-8 py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center disabled:opacity-60 whitespace-nowrap"
                  >
                    {status === 'submitting' ? t('contact.form.sending') : t('contact.form.submit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* ===== SECTION 3 — OFFICE LOCATIONS ===== */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12 lg:px-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bangladesh */}
              <div className="flex flex-col gap-4 rounded-2xl overflow-hidden border border-[#F0EFED]">
                <div className="h-52 md:h-60 bg-[#F0EFED] relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d233668.38704001157!2d90.2791927058338!3d23.7807777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b8b087026b81%3A0x8fa563bbdd5904c2!2sDhaka%2C%20Bangladesh!5e0!3m2!1sen!2sus!4v1716451200000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Dhaka Office Location"
                    className="absolute inset-0"
                  />
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <h3 className="font-serif text-lg font-bold text-[#2A2A2A]">{t('contact.offices.bd.title')}</h3>
                  <p className="text-sm text-[#2A2A2A] font-medium">{t('contact.offices.bd.company')}</p>
                  <p className="text-sm text-[#8A8A8A] font-light">{t('contact.offices.bd.address')}</p>
                </div>
              </div>

              {/* Korea HQ */}
              <div className="flex flex-col gap-4 rounded-2xl overflow-hidden border border-[#F0EFED]">
                <div className="h-52 md:h-60 bg-[#F0EFED] relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d101298.96984807027!2d126.82762146968459!3d37.38002317839487!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357b6a2a1b5b5b5b%3A0x0!2sAnyang-si%2C%20Gyeonggi-do%2C%20South%20Korea!5e0!3m2!1sen!2sus!4v1716451200000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Korea Headquarters Location"
                    className="absolute inset-0"
                  />
                </div>
                <div className="p-6 flex flex-col gap-2">
                  <h3 className="font-serif text-lg font-bold text-[#2A2A2A]">{t('contact.offices.korea.title')}</h3>
                  <p className="text-sm text-[#2A2A2A] font-medium">{t('contact.offices.korea.company')}</p>
                  <p className="text-sm text-[#8A8A8A] font-light">{t('contact.offices.korea.address')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SharedFooter />
      </main>
    </>
  );
}